import { Alert, AlertTitle, Button, FormGroup, FormLabel, MenuItem, Select, TextField } from "@mui/material";
import { FC, useEffect, useMemo, useState } from "react";
import { Action, createPsychologistAction, CreatePsychologistActionPayload, getActions } from "src/apis/action";
import CompanySelect from "src/pages/components/CompanySelect";
import PageContainer from "src/pages/components/PageContainer";
import { isUserPsychologist } from "src/utils/auth";
import { formatCurrency } from "src/utils/currency";
import { Id } from "src/utils/types";

const initialValues: CreatePsychologistActionPayload = {
  action_id: '',
  data: '',
  id_empresa: '',
  titulo: '',
  numero_participantes: '',
};

const RegisterAction: FC = (): JSX.Element => {
  const [actions, setActions] = useState<Action[]>([]);
  const [error, setError] = useState<string>('');
  const [success, setSuccess] = useState<boolean>(false);
  const [formValues, setFormValues] = useState<CreatePsychologistActionPayload>(initialValues);

  const resetFormState = () => {
    setError('');
    setSuccess(false);
    setFormValues(initialValues);
  }

  const fetchActions = async (): Promise<void> => {
    try {
      const actionsResponse = await getActions();

      setActions(actionsResponse);
      setError('')
    } catch (err: any) {
      setError(err?.message)
    }
  }

  useEffect(() => {
    fetchActions()
  }, []);

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    try {
      await createPsychologistAction(formValues);

      setError('');
      setSuccess(true);
    } catch (err: any) {
      setError(err?.message);
      setSuccess(false);
    }
  };

  const isSubmitDisabled = useMemo(() => {
    if (success) return true;

    const requiredFields: (keyof CreatePsychologistActionPayload)[] = [
      "action_id",
      "data",
      "id_empresa",
      "titulo",
    ];

    return !requiredFields.every((field) => formValues[field].trim() !== "");
  }, [success, formValues]);

  return (
    <PageContainer title="Adicionar ação realizada" error={error} guard={isUserPsychologist}>
      <form
        onSubmit={handleSubmit}
        style={{ display: "flex", flexDirection: "column", gap: "1rem" }}
      >
        <CompanySelect
          companyId={formValues.id_empresa}
          setCompanyId={(companyId: Id) => {
            setFormValues(previousValues => ({
              ...previousValues,
              id_empresa: companyId as string,
            }));
            setSuccess(false);
          }}
          setError={setError}
        />
        <FormGroup>
          <FormLabel>Título</FormLabel>
          <TextField
            name="titulo"
            placeholder="Título da ação realizada"
            value={formValues.titulo}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                titulo: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Ação</FormLabel>
          <Select
            labelId="action_id"
            name="action_id"
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                action_id: e.target.value as string,
              }));
            }}
            variant="outlined"
            value={formValues?.action_id ?? ''}
          >
            {actions.map((action) => (
              <MenuItem key={action.id} value={String(action.id)}>
                {action.acao} - {formatCurrency(action.remuneracao)}
              </MenuItem>
            ))}
          </Select>
        </FormGroup>
        <FormGroup>
          <FormLabel>Atendimento</FormLabel>
          <TextField
            name="data"
            type="date"
            value={formValues.data}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                data: e.target.value as string,
              }));
            }}
          />
        </FormGroup>
        <FormGroup>
          <FormLabel>Número de participantes</FormLabel>
          <TextField
            name="numero_participantes"
            type="number"
            inputProps={{ min: 0 }}
            value={formValues.numero_participantes}
            onChange={(e) => {
              setFormValues(previousValues => ({
                ...previousValues,
                numero_participantes: e.target.value as string,
              }));
            }}
          />
        </FormGroup>

        {Boolean(success) && (
          <Alert severity="success">
            <AlertTitle>
              Ação cadastrada com sucesso!
            </AlertTitle>
          </Alert>
        )}

        {success ? (
          <Button
            type="button"
            variant="contained"
            onClick={() => {
              resetFormState();
            }}
          >
            Cadastrar nova ação
          </Button>
        ) : (
          <Button
            disabled={isSubmitDisabled}
            type="submit"
            variant="contained"
          >
            Salvar
          </Button>
        )}

      </form>
    </PageContainer>
  )
}

export default RegisterAction;
